import React from 'react'
import { Box, IconButton, Snackbar, type SnackbarOrigin, Typography } from '@mui/material'
import { alpha } from '@mui/material/styles'

import {
  borderRadius,
  colors,
  lightTokens,
  maxWidth,
  minWidth,
  shadows,
  spacing,
  toastDurations,
  typography,
} from '~tokens/internal'

import Icon from './Icon'

export const toastAnchorOrigin = {
  bottomRight: { vertical: 'bottom', horizontal: 'right' } as SnackbarOrigin,
} as const

const toastIconMap = {
  success: 'success',
  info: 'info',
  error: 'error',
  warning: 'warning',
  testMode: 'science',
  aiRecommendation: 'autoAwesome',
  upgrade: 'autoMode',
  feedback: 'campaign',
} as const

export type ToastType = keyof typeof toastIconMap

const toastTokenMap: Record<ToastType, { main: string; lighter: string }> = {
  success: lightTokens.success,
  info: lightTokens.info,
  error: lightTokens.error,
  warning: lightTokens.warning,
  testMode: lightTokens.testMode,
  aiRecommendation: lightTokens.aiRecommendation,
  upgrade: lightTokens.upgrade,
  feedback: lightTokens.feedback,
}

function getToastStyles(type: ToastType): {
  bgColor: string
  textColor: string
  iconColor: string
} {
  const tokenColors = toastTokenMap[type]

  return {
    bgColor: tokenColors.main,
    textColor: lightTokens.primary.contrastText,
    iconColor: lightTokens.primary.contrastText,
  }
}

const toastContentSx = {
  flex: 1,
  display: 'flex',
  alignItems: 'center',
} as const

interface ToastProps {
  type?: ToastType
  message: React.ReactNode
  open: boolean
  onClose: () => void
  autoHideDuration?: number
  anchorOrigin?: SnackbarOrigin
  action?: React.ReactNode
}

const Toast: React.FC<ToastProps> = ({
  type = 'info',
  message,
  open,
  onClose,
  autoHideDuration = toastDurations.default,
  anchorOrigin = toastAnchorOrigin.bottomRight,
  action,
}) => {
  const iconName = toastIconMap[type]
  const styles = getToastStyles(type)

  return (
    <Snackbar
      open={open}
      autoHideDuration={autoHideDuration}
      onClose={(_event, reason) => {
        if (reason === 'clickaway') return
        onClose()
      }}
      anchorOrigin={anchorOrigin}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: spacing.xxs,
          py: spacing.xxs,
          px: spacing.xs,
          background: styles.bgColor,
          borderRadius: borderRadius.sm,
          boxShadow: shadows.menu,
          minWidth: minWidth.toast,
          maxWidth: maxWidth.toast,
        }}
      >
        <Box sx={{ color: styles.iconColor, display: 'flex', alignItems: 'center' }}>
          <Icon name={iconName} size={typography.fontSize.md} />
        </Box>
        <Box sx={toastContentSx}>
          <Typography
            sx={{
              color: styles.textColor,
              fontWeight: typography.fontWeight.medium,
              fontSize: typography.fontSize.xs,
              lineHeight: typography.lineHeight.normal,
              letterSpacing: typography.letterSpacing.wide,
            }}
          >
            {message}
          </Typography>
        </Box>
        {action && (
          <Box
            sx={{
              color: styles.textColor,
              '& .MuiButton-root': {
                color: styles.textColor,
                '&:hover': {
                  backgroundColor: alpha(styles.iconColor, colors.opacity.hoverFill),
                },
              },
            }}
          >
            {React.Children.map(action, (child) =>
              React.isValidElement(child) ? React.cloneElement(child, { size: 'small' }) : child,
            )}
          </Box>
        )}
        <IconButton size='small' onClick={onClose} sx={{ color: styles.iconColor }}>
          <Icon name='close' size={typography.fontSize.md} />
        </IconButton>
      </Box>
    </Snackbar>
  )
}

export default Toast
